import iconUrl from '../Assets/Logo/logo-icon.svg'
import wordmarkUrl from '../Assets/Logo/logo-wordmark.svg'
import { t } from '../i18n'
import { cellKey, formatTime, longDate } from './crossword'

/* The crossword result as a picture to share or copy: 1080 x 1350 (4:5, fits
   Instagram, WhatsApp and X). Logo, the day, the grid's shape with every
   square orange (no letters, so it gives nothing away; revealed squares
   blue), the time, up to three tags (record, rank, streak, help), the link.
   Drawn on a canvas in the page's own Inter. */
const W = 1080
const H = 1350
const PAD = 80
const C = {
  bg: '#FFF6EC',
  ink: '#0F1419',
  muted: '#536471',
  orange: '#E2690A',
  orangeText: '#C2570A',
  tagText: '#A8480A',
  tagBorder: '#F6DCC0',
  revealed: '#2B4C8C',
  white: '#FFFFFF',
}
const FONT = (weight, size) =>
  `${weight} ${size}px Inter, system-ui, -apple-system, 'Segoe UI', Roboto, Arial, sans-serif`

function loadImage(src) {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = src
  })
}

async function loadFonts() {
  if (!document.fonts?.load) return
  const wanted = [FONT(800, 76), FONT(700, 32), FONT(600, 40), FONT(500, 38)].map((f) =>
    document.fonts.load(f, 'Križaljka 0123456789')
  )
  // A slow font server shouldn't hold the picture up: system fonts after 1.5 s.
  await Promise.race([Promise.all(wanted), new Promise((r) => setTimeout(r, 1500))]).catch(() => {})
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath()
  ctx.moveTo(x + r, y)
  ctx.arcTo(x + w, y, x + w, y + h, r)
  ctx.arcTo(x + w, y + h, x, y + h, r)
  ctx.arcTo(x, y + h, x, y, r)
  ctx.arcTo(x, y, x + w, y, r)
  ctx.closePath()
}

/* The largest font size (from `size` down) at which `text` fits in `width`. */
function fit(ctx, text, weight, size, width) {
  let s = size
  ctx.font = FONT(weight, s)
  while (s > 20 && ctx.measureText(text).width > width) {
    s -= 4
    ctx.font = FONT(weight, s)
  }
  return s
}

/* Tags under the time: what the solve earned, most notable first. */
export function cardTags({ result, stats, helped }) {
  const tags = []
  if (result?.isRecord) tags.push({ text: t('crossword.newRecord'), strong: true })
  if (helped) tags.push({ text: t('crossword.cardHelped') })
  else if (result?.ranked && result.rank === 1) tags.push({ text: t('crossword.cardFastest') })
  else if (result?.ranked && result.fasterThanPercent != null)
    tags.push({ text: t('crossword.cardFaster', { percent: result.fasterThanPercent }) })
  if (stats?.currentStreak >= 2)
    tags.push({ text: t('crossword.cardStreak', { count: stats.currentStreak }) })
  return tags.slice(0, 3)
}

function drawCard(ctx, { puzzle, seconds, revealed, tags, icon, wordmark }) {
  ctx.fillStyle = C.bg
  ctx.fillRect(0, 0, W, H)
  ctx.textBaseline = 'alphabetic'

  // Logo, the date on the right.
  const iconSize = 84
  if (icon) ctx.drawImage(icon, PAD, PAD, iconSize, iconSize)
  if (wordmark) {
    const h = 50
    const w = (h * wordmark.naturalWidth) / wordmark.naturalHeight || h * 4.37
    ctx.drawImage(wordmark, PAD + iconSize + 22, PAD + (iconSize - h) / 2, w, h)
  }
  ctx.font = FONT(600, 34)
  ctx.fillStyle = C.muted
  ctx.textAlign = 'right'
  ctx.fillText(
    puzzle.date.split('-').reverse().map(Number).join('. ') + '.',
    W - PAD,
    PAD + iconSize / 2 + 12
  )

  // Title and day.
  ctx.textAlign = 'left'
  ctx.fillStyle = C.ink
  fit(ctx, t('crossword.documentTitle'), 800, 76, W - 2 * PAD)
  ctx.fillText(t('crossword.documentTitle'), PAD, 290)
  ctx.font = FONT(500, 38)
  ctx.fillStyle = C.muted
  const day = longDate(puzzle.date)
  ctx.fillText(day.charAt(0).toUpperCase() + day.slice(1), PAD, 345)

  // The grid's shape.
  const gap = 10
  const area = 560
  const cell = Math.floor(
    (area - gap * (Math.max(puzzle.rows, puzzle.cols) - 1)) / Math.max(puzzle.rows, puzzle.cols)
  )
  const gw = cell * puzzle.cols + gap * (puzzle.cols - 1)
  const x0 = (W - gw) / 2
  const y0 = 400
  for (let r = 0; r < puzzle.rows; r++) {
    for (let c = 0; c < puzzle.cols; c++) {
      ctx.fillStyle = puzzle.isBlack(r, c) ? C.ink : revealed[cellKey(r, c)] ? C.revealed : C.orange
      roundRect(ctx, x0 + c * (cell + gap), y0 + r * (cell + gap), cell, cell, 14)
      ctx.fill()
    }
  }

  // The time.
  ctx.textAlign = 'center'
  ctx.font = FONT(600, 40)
  ctx.fillStyle = C.muted
  ctx.fillText(t('crossword.cardSolvedIn'), W / 2, 1030)
  ctx.font = FONT(800, 140)
  ctx.fillStyle = C.ink
  ctx.fillText(formatTime(seconds), W / 2, 1160)

  // Tags, centred in one row; the last ones go when they don't fit.
  ctx.font = FONT(700, 32)
  const tagH = 62
  const tagPad = 28
  const tagGap = 14
  let row = tags.map((tag) => ({ ...tag, w: ctx.measureText(tag.text).width + 2 * tagPad }))
  const rowWidth = (items) =>
    items.reduce((s, tag) => s + tag.w, 0) + tagGap * Math.max(0, items.length - 1)
  while (row.length > 1 && rowWidth(row) > W - 2 * PAD) row = row.slice(0, -1)
  let x = (W - rowWidth(row)) / 2
  const ty = 1196
  row.forEach((tag) => {
    roundRect(ctx, x, ty, tag.w, tagH, tagH / 2)
    ctx.fillStyle = tag.strong ? C.orange : C.white
    ctx.fill()
    if (!tag.strong) {
      ctx.lineWidth = 2
      ctx.strokeStyle = C.tagBorder
      ctx.stroke()
    }
    ctx.fillStyle = tag.strong ? C.white : C.tagText
    ctx.fillText(tag.text, x + tag.w / 2, ty + 42)
    x += tag.w + tagGap
  })

  // The link.
  ctx.font = FONT(700, 32)
  ctx.fillStyle = C.orangeText
  ctx.fillText(t('crossword.cardLink'), W / 2, H - 52)
}

const toBlob = (canvas) =>
  new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('no image'))), 'image/png')
    } catch (e) {
      reject(e)
    }
  })

/* Promise of the PNG. { puzzle, seconds, revealed: {"r,c": true}, tags } */
export async function crosswordCard({ puzzle, seconds, revealed, tags }) {
  const [, icon, wordmark] = await Promise.all([
    loadFonts(),
    loadImage(iconUrl),
    loadImage(wordmarkUrl),
  ])
  const draw = (logo) => {
    const canvas = document.createElement('canvas')
    canvas.width = W
    canvas.height = H
    drawCard(canvas.getContext('2d'), { puzzle, seconds, revealed, tags, ...logo })
    return toBlob(canvas)
  }
  try {
    return await draw({ icon, wordmark })
  } catch (e) {
    // A browser that won't export a canvas with the SVG logo on it: without.
    return draw({ icon: null, wordmark: null })
  }
}
